import {
  useGetBulkSessionData,
  useGetCanonicalCategoriesByEvent,
  useGetCanonicalTagsByEvent,
  useGetSessionsByEventId,
} from '@sunrise-ui/api-client';
import {
  Colors,
  Tag as TagComponent,
  TagSkeletons,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import React, { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';

type Props = {
  sessionId: string;
  eventId: string;
};

// Define unified types for tag display items
interface DisplayTagItem {
  itemType: 'tag' | 'category';
  id: string;
  name: string;
}

// Import proper API types to ensure type safety
import type { SessionCategory, SessionTag } from '@sunrise-ui/api/events/api';

const OVERFLOW_PILL_SMALL_WIDTH = 50.47;
const OVERFLOW_PILL_LARGE_WIDTH = 58.52;
const TAG_GAP = 8;

export function SessionCardTags({ sessionId, eventId }: Props) {
  // Get all sessions from the event to ensure full bulk data cache
  const { data: eventSessions } = useGetSessionsByEventId(eventId);
  const sessionIds =
    eventSessions?.map((session) => session.id).filter(Boolean) || [];

  // Use the bulk session data hook with ALL sessionIds from the event
  // This ensures the cache is fully populated and prevents partial data loss after navigation
  const { data: bulkSessionData, isLoading: isLoadingBulkData } =
    useGetBulkSessionData({
      sessionIds,
    });

  // Get canonical tags and categories for cross-referencing
  // These are also cached by the voting page
  const { data: eventTags, isLoading: isLoadingEventTags } =
    useGetCanonicalTagsByEvent(eventId);
  const { data: eventCategories, isLoading: isLoadingEventCategories } =
    useGetCanonicalCategoriesByEvent(eventId);

  // Extract tags and categories from bulk query
  const bulkSessionInfo = bulkSessionData?.session_data?.find(
    (sessionData: { session_id: string }) =>
      sessionData.session_id === sessionId,
  );

  // Get tags and categories from bulk data
  const tags = bulkSessionInfo?.data?.session_tags || [];
  const categories = bulkSessionInfo?.data?.session_categories || [];

  // Data structure matches what the useGetSessionTagsAndCategories hook would return
  const data = {
    tags,
    categories,
  };

  // Loading state includes both bulk data and canonical data
  const isLoadingTags =
    isLoadingBulkData || isLoadingEventTags || isLoadingEventCategories;

  const [containerWidth, setContainerWidth] = useState(0);
  const [tagsWidth, setTagsWidth] = useState<Record<string, number>>({});

  // Combine tags and categories into a single array for display, preserving their type
  const tagItems = useMemo<DisplayTagItem[]>(() => {
    if (!data) return [];
    const { tags = [], categories = [] } = data;

    // Process tags using canonical event tags for lookup
    const tagItemsWithType: DisplayTagItem[] = tags.map(
      (sessionTag: SessionTag) => {
        // Use proper SessionTag type - no casting needed
        const tagId = sessionTag.tag_id;

        // Look up tag name from canonical event tags
        const canonicalTag = eventTags?.find((tag) => tag.id === tagId);
        // Use canonical tag name as fallback
        const tagName = canonicalTag?.name || '';

        return {
          itemType: 'tag',
          id: tagId,
          name: tagName,
        };
      },
    );

    // Process categories using canonical event categories for lookup
    const categoryItemsWithType: DisplayTagItem[] = categories.map(
      (sessionCategory: SessionCategory) => {
        // Use proper SessionCategory type - no casting needed
        const categoryId = sessionCategory.category_id;

        // Look up category name from canonical event categories
        const canonicalCategory = eventCategories?.find(
          (category) => category.id === categoryId,
        );
        // Use canonical category name as fallback
        const categoryName = canonicalCategory?.name || '';

        return {
          itemType: 'category',
          id: categoryId,
          name: categoryName,
        };
      },
    );

    return [...categoryItemsWithType, ...tagItemsWithType] as DisplayTagItem[];
  }, [data]);

  const { numberOfTagsFirstRow, numberOfTagsSecondRow, numberOfHiddenTags } =
    useMemo(() => {
      const numberOfTags = tagItems?.length ?? 0;

      if (isLoadingTags || !tagItems) {
        return {
          numberOfTagsFirstRow: 0,
          numberOfTagsSecondRow: 0,
          numberOfHiddenTags: 0,
        };
      }

      // Check if we have container width and some measurements
      const hasContainerWidth = containerWidth > 0;
      const measurementsCount = Object.keys(tagsWidth).length;
      const hasAnyMeasurements = measurementsCount > 0;

      // If no container width, keep in measurement phase
      if (!hasContainerWidth) {
        return {
          numberOfTagsFirstRow: numberOfTags,
          numberOfTagsSecondRow: 0,
          numberOfHiddenTags: 0,
        };
      }

      // If we have container width but no measurements yet, show all for initial measurement
      if (!hasAnyMeasurements) {
        return {
          numberOfTagsFirstRow: numberOfTags,
          numberOfTagsSecondRow: 0,
          numberOfHiddenTags: 0,
        };
      }

      let numberOfTagsFirstRow = 0;
      let numberOfTagsSecondRow = 0;
      let tagsWidthFirstRowSum = 0;
      let tagsWidthSecondRowSum = 0;

      for (let i = 0; i < numberOfTags; i++) {
        const width = tagsWidth[tagItems[i].id!];
        if (tagsWidthFirstRowSum + width > containerWidth) break;

        tagsWidthFirstRowSum += width + TAG_GAP;
        numberOfTagsFirstRow += 1;
      }

      for (let i = numberOfTagsFirstRow; i < numberOfTags; i++) {
        const width = tagsWidth[tagItems[i].id!];
        const overflowPillWidth =
          numberOfTags - numberOfTagsFirstRow - numberOfTagsSecondRow < 10
            ? OVERFLOW_PILL_SMALL_WIDTH
            : OVERFLOW_PILL_LARGE_WIDTH;
        if (
          tagsWidthSecondRowSum + width >
          containerWidth - overflowPillWidth - TAG_GAP
        )
          break;

        tagsWidthSecondRowSum += width + TAG_GAP;
        numberOfTagsSecondRow += 1;
      }

      return {
        numberOfTagsFirstRow,
        numberOfTagsSecondRow,
        numberOfHiddenTags:
          numberOfTags - numberOfTagsFirstRow - numberOfTagsSecondRow,
      };
    }, [containerWidth, tagsWidth, isLoadingTags, tagItems]);

  const styles = useDynamicStyles(createStyles);

  return (
    <View
      style={styles.tagsContainer}
      onLayout={(e) => {
        setContainerWidth(e.nativeEvent.layout.width);
      }}
    >
      <View style={styles.row}>
        {isLoadingTags ? (
          <TagSkeletons count={2} />
        ) : (
          tagItems?.map((item, index) => (
            <TagComponent
              key={`${sessionId}-${item.id}-${index}`}
              id={item.id}
              name={item.name}
              type={item.itemType}
              isVisible={index < numberOfTagsFirstRow}
              onLayout={(e) => {
                const { width } = e.nativeEvent.layout;
                setTagsWidth((prev) => ({
                  ...prev,
                  [item.id!]: width,
                }));
              }}
            />
          ))
        )}
      </View>
      <View style={styles.row}>
        {isLoadingTags ? (
          <TagSkeletons count={1} />
        ) : (
          tagItems
            ?.slice(
              numberOfTagsFirstRow,
              numberOfTagsFirstRow + numberOfTagsSecondRow,
            )
            .map((item, index) => (
              <TagComponent
                key={`${sessionId}-${item.id}-${numberOfTagsFirstRow + index}`}
                id={item.id}
                name={item.name}
                type={item.itemType}
              />
            ))
        )}
        {!isLoadingTags && numberOfHiddenTags > 0 && (
          <TagComponent
            style={styles.overflowPill}
            name={`+${numberOfHiddenTags} more`}
          />
        )}
      </View>
    </View>
  );
}

const createStyles = () =>
  StyleSheet.create({
    tagsContainer: {
      flexShrink: 1,
      width: '100%',
      justifyContent: 'space-between',
      gap: TAG_GAP,
    },
    row: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      gap: TAG_GAP,
    },
    overflowPill: {
      paddingVertical: 2,
      backgroundColor: Colors.purple0,
    },
    tagText: {
      fontSize: 12,
    },
  });
